/**
 * ALFA TANGO 1 — único lugar con flujos «Visita» y «Funcionarios» (cédula, hacia dónde va en visita, catálogo en funcionarios).
 *
 * No es «Alfa Tango 2» (otro producto / otro archivo cuando exista) ni el flujo de «Alfa Tango 3» (`alfa_tango_3`, guía, etc.).
 * En BD se guarda `tipo_evento` = ALFA_TANGO_1.
 *
 * Catálogo: solo `alfa_tango_1`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import envConfig from '../config/environment';

const VISTA1 = 'vista1';
const VISTA2 = 'vista2';
const CAPTURA = 'captura';

const FLUJO_VISITA = 'visita';
const FLUJO_FUNCIONARIOS = 'funcionarios';

const OcrSpinnerRow = () => (
  <div className="flex items-center gap-2 text-sm text-indigo-600 py-1" role="status">
    <span className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" aria-hidden />
    <span>Extrayendo datos de la cédula…</span>
  </div>
);

const MAX_IMAGE_WIDTH = 1200;
const JPEG_QUALITY = 0.82;

const parseCedulaFromDescripcion = (desc) => {
  if (!desc || typeof desc !== 'string') return '';
  const digits = desc.replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
};

const isMobileDeviceForCamera = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > MAX_IMAGE_WIDTH) {
        height = (height * MAX_IMAGE_WIDTH) / width;
        width = MAX_IMAGE_WIDTH;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' }));
          else reject(new Error('Compresión fallida'));
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error cargando imagen'));
    };
    img.src = url;
  });
};

const AlfaTango1 = () => {
  const { user } = useAuth();
  const [view, setView] = useState(VISTA1);
  const [flow, setFlow] = useState(null);
  const [vista2Option, setVista2Option] = useState(null);
  const [observacionVisita, setObservacionVisita] = useState('');

  const [fotoCedula, setFotoCedula] = useState(null);
  const [fotoCedulaPreview, setFotoCedulaPreview] = useState('');
  const [nombreCedula, setNombreCedula] = useState('');
  const [cedulaNum, setCedulaNum] = useState('');
  const [loadingOcr, setLoadingOcr] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pasoCapturaVisita, setPasoCapturaVisita] = useState('cedula');
  const [pasoCapturaFuncionarios, setPasoCapturaFuncionarios] = useState('elegir');
  const [cameraAbierta, setCameraAbierta] = useState(false);
  const [catalogoFuncionarios, setCatalogoFuncionarios] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [selectedFuncionarioCatalog, setSelectedFuncionarioCatalog] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const inputCedulaRef = useRef(null);
  const canvasRef = useRef(null);

  const apiOrigin = (() => {
    try {
      if (typeof envConfig.apiUrl === 'string' && envConfig.apiUrl) {
        return new URL(envConfig.apiUrl).origin;
      }
      if (typeof window !== 'undefined') return `${window.location.protocol}//${window.location.hostname}:3004`;
    } catch (e) {}
    return '';
  })();

  const useDeviceCamera = isMobileDeviceForCamera();

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraAbierta(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    if (!cameraAbierta || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    video.srcObject = stream;
    video.play().catch(() => {});
  }, [cameraAbierta]);

  useEffect(() => {
    if (view !== CAPTURA || flow !== FLUJO_FUNCIONARIOS || pasoCapturaFuncionarios !== 'elegir' || !user?.id_generado) return;
    let cancelled = false;
    setLoadingCatalogo(true);
    const url = `${getApiUrl('SUPERVISION', 'CATALOGO')}?id_generado=${encodeURIComponent(user.id_generado)}`;
    fetch(url, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.success) return;
        const list = (data.data || []).filter((item) => item.tipo === 'FUNCIONARIO');
        setCatalogoFuncionarios(list);
      })
      .catch(() => { if (!cancelled) setCatalogoFuncionarios([]); })
      .finally(() => { if (!cancelled) setLoadingCatalogo(false); });
    return () => { cancelled = true; };
  }, [view, flow, pasoCapturaFuncionarios, user?.id_generado]);

  const startCameraDesktop = useCallback(async () => {
    stopCamera();
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setCameraAbierta(true);
    } catch (err) {
      setError('No se pudo acceder a la cámara.');
      setCameraAbierta(false);
    }
  }, [stopCamera]);

  const captureFromVideo = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.srcObject) return null;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], 'captura.jpg', { type: 'image/jpeg' }));
          else resolve(null);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    });
  }, []);

  const runOcr = useCallback(async (file) => {
    if (!file) return { nombre: '', cedula: '' };
    setLoadingOcr(true);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      const url = getApiUrl('BITACORA', 'EXTRACT_CEDULA');
      const res = await fetch(url, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success && (data.nombre != null || data.cedula != null)) {
        return {
          nombre: (data.nombre != null ? String(data.nombre) : '').trim(),
          cedula: (data.cedula != null ? String(data.cedula) : '').trim()
        };
      }
      return { nombre: '', cedula: '' };
    } catch (e) {
      return { nombre: '', cedula: '' };
    } finally {
      setLoadingOcr(false);
    }
  }, []);

  const handleCaptureCedula = useCallback(async (file, nextStep) => {
    if (!file) return;
    setError('');
    stopCamera();
    try {
      const compressed = await compressImage(file);
      setFotoCedula(compressed);
      setFotoCedulaPreview(URL.createObjectURL(compressed));
      if (nextStep) nextStep();
      runOcr(compressed).then(({ nombre, cedula }) => {
        setNombreCedula((n) => n || nombre);
        setCedulaNum((c) => c || cedula);
      }).catch(() => {});
    } catch (e) {
      setError('Error al procesar la imagen.');
    }
  }, [runOcr, stopCamera]);

  const resetToStart = useCallback(() => {
    stopCamera();
    setView(VISTA1);
    setFlow(null);
    setVista2Option(null);
    setPasoCapturaVisita('cedula');
    setPasoCapturaFuncionarios('elegir');
    setSelectedFuncionarioCatalog(null);
    setObservacionVisita('');
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setNombreCedula('');
    setCedulaNum('');
    setShowConfirmModal(false);
    setError('');
    setSuccess('');
  }, [stopCamera]);

  const handleVista1Select = (flujo) => {
    setFlow(flujo);
    setError('');
    setSuccess('');
    setFotoCedula(null);
    setFotoCedulaPreview('');
    setNombreCedula('');
    setCedulaNum('');
    setObservacionVisita('');
    setPasoCapturaVisita('cedula');
    setPasoCapturaFuncionarios('elegir');
    if (flujo === FLUJO_VISITA) setView(CAPTURA);
    else {
      setVista2Option(null);
      setView(CAPTURA);
    }
  };

  const getVolverClick = () => {
    if (view === VISTA2) return resetToStart;
    if (view === CAPTURA && flow === FLUJO_VISITA) {
      return pasoCapturaVisita === 'revision'
        ? () => setPasoCapturaVisita('descripcion')
        : pasoCapturaVisita === 'descripcion'
          ? () => setPasoCapturaVisita('cedula')
          : resetToStart;
    }
    if (view === CAPTURA && flow === FLUJO_FUNCIONARIOS) {
      return pasoCapturaFuncionarios === 'revision'
        ? () => setPasoCapturaFuncionarios('cedula')
        : pasoCapturaFuncionarios === 'cedula'
          ? () => { setPasoCapturaFuncionarios('elegir'); setFotoCedula(null); setFotoCedulaPreview(''); setNombreCedula(''); setCedulaNum(''); setSelectedFuncionarioCatalog(null); }
          : resetToStart;
    }
    return () => {};
  };

  const openConfirmModal = () => {
    setError('');
    if (!fotoCedula && !(flow === FLUJO_FUNCIONARIOS && selectedFuncionarioCatalog?.foto_url && parseCedulaFromDescripcion(selectedFuncionarioCatalog?.Descripcion))) {
      setError('Debe tomar la foto de la cédula.');
      return;
    }
    if (!nombreCedula.trim()) {
      setError('No se puede guardar: falta completar Nombre.');
      return;
    }
    if (!cedulaNum.trim()) {
      setError('No se puede guardar: falta completar Cédula.');
      return;
    }
    if (flow === FLUJO_VISITA && !observacionVisita.trim()) {
      setError('No se puede guardar: falta completar Hacia dónde va.');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleSubmitVisita = async () => {
    setError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('idGenerado', user?.id_generado ?? '');
      formData.append('tipo', 'ALFA_TANGO_1');
      formData.append('flujoTango2', 'VISITA');
      formData.append('observaciones', observacionVisita.trim());
      formData.append('p_nombre', nombreCedula.trim());
      formData.append('p_cedula', cedulaNum.trim());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      if (fotoCedula) {
        formData.append('foto', fotoCedula);
      }

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }
      setSuccess('Registro guardado correctamente.');
      setShowConfirmModal(false);
      setTimeout(resetToStart, 1200);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFuncionarios = async () => {
    setError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('idGenerado', user?.id_generado ?? '');
      formData.append('tipo', 'ALFA_TANGO_1');
      formData.append('flujoTango2', 'FUNCIONARIOS');
      formData.append('p_nombre', nombreCedula.trim());
      formData.append('p_cedula', cedulaNum.trim());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');

      const usarFotoCatalogo = selectedFuncionarioCatalog?.foto_url && !fotoCedula;
      if (usarFotoCatalogo) {
        formData.append('foto_url_catalogo', selectedFuncionarioCatalog.foto_url);
      } else if (fotoCedula) {
        formData.append('foto', fotoCedula);
      }

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }

      const fotoUrlGuardada = data.foto_url || null;
      if (!selectedFuncionarioCatalog && fotoUrlGuardada) {
        const catalogUrl = getApiUrl('SUPERVISION', 'CATALOGO');
        await fetch(catalogUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id_generado: user?.id_generado,
            dato: nombreCedula.trim(),
            descripcion: cedulaNum.trim() ? `Alfa Tango 1-${cedulaNum.trim()}` : 'Alfa Tango 1',
            tipo: 'FUNCIONARIO',
            foto_url: fotoUrlGuardada
          })
        });
      } else if (selectedFuncionarioCatalog && fotoCedula && fotoUrlGuardada) {
        const catalogUpdateUrl = `${getApiUrl('SUPERVISION', 'CATALOGO')}/${selectedFuncionarioCatalog.id}`;
        const descBase = (selectedFuncionarioCatalog.Descripcion || '').replace(/\d{10}\s*$/, '').trim();
        await fetch(catalogUpdateUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id_generado: user?.id_generado,
            descripcion: cedulaNum.trim() ? `${descBase}-${cedulaNum.trim()}` : descBase || 'Alfa Tango 1',
            foto_url: fotoUrlGuardada
          })
        });
      }

      setSuccess('Registro guardado correctamente.');
      setShowConfirmModal(false);
      setTimeout(resetToStart, 1200);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGuardarVista = async () => {
    if (flow === FLUJO_VISITA) {
      await handleSubmitVisita();
      return;
    }
    if (flow === FLUJO_FUNCIONARIOS) {
      await handleSubmitFuncionarios();
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setSuccess('Registro listo en vista previa (sin inserción en base).');
      setShowConfirmModal(false);
      setTimeout(resetToStart, 1200);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="h-full min-h-full w-full flex flex-col overflow-auto p-3 sm:p-4 md:p-6">
        <div className="w-full flex flex-col flex-1 min-h-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Alfa Tango 1</h1>
          <p className="text-sm text-gray-500 mb-4">Flujos de Visita y Funcionarios.</p>

          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>}

          {flow != null && (view === VISTA2 || view === CAPTURA) && (
            <button type="button" onClick={getVolverClick()} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium self-start mb-2">
              ← Volver
            </button>
          )}

          {view === VISTA1 && (
            <div className="grid gap-3">
              <button
                type="button"
                onClick={() => handleVista1Select(FLUJO_VISITA)}
                className="w-full flex items-center justify-center px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400 transition-colors duration-200 shadow-sm"
              >
                Visita
              </button>
              <button
                type="button"
                onClick={() => handleVista1Select(FLUJO_FUNCIONARIOS)}
                className="w-full flex items-center justify-center px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400 transition-colors duration-200 shadow-sm"
              >
                Funcionarios
              </button>
            </div>
          )}

          {view === CAPTURA && flow === FLUJO_VISITA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              {pasoCapturaVisita === 'cedula' && (
                <div className="space-y-4">
                  <p className="text-2xl font-bold text-gray-900 mb-1">Capturar cédula</p>
                  <p className="text-sm text-gray-600">Tome la foto de la cédula del visitante.</p>
                  {!cameraAbierta ? (
                    <div className="flex flex-col items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (useDeviceCamera) inputCedulaRef.current?.click();
                          else startCameraDesktop();
                        }}
                        className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md"
                      >
                        Capturar
                      </button>
                    </div>
                  ) : (
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                      <canvas ref={canvasRef} className="hidden" />
                      <button
                        type="button"
                        onClick={async () => {
                          const f = await captureFromVideo();
                          if (f) handleCaptureCedula(f, () => setPasoCapturaVisita('descripcion'));
                        }}
                        className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow"
                      >
                        Capturar
                      </button>
                    </div>
                  )}
                  <input
                    ref={inputCedulaRef}
                    type="file"
                    accept="image/*"
                    capture={useDeviceCamera ? 'environment' : undefined}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleCaptureCedula(f, () => setPasoCapturaVisita('descripcion'));
                      e.target.value = '';
                    }}
                  />
                </div>
              )}

              {pasoCapturaVisita === 'descripcion' && (
                <div className="space-y-4">
                  <p className="text-2xl font-bold text-gray-900 mb-1">Hacia dónde va</p>
                  <textarea
                    value={observacionVisita}
                    onChange={(e) => setObservacionVisita(e.target.value)}
                    placeholder="Ej. Oficina 101, reunión con ventas..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button type="button" onClick={() => setPasoCapturaVisita('revision')} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700">
                    Continuar
                  </button>
                </div>
              )}

              {pasoCapturaVisita === 'revision' && (
                <div className="space-y-4">
                  <p className="text-2xl font-bold text-gray-900 mb-1">Revisar datos</p>
                  <p className="text-sm text-gray-600">Complete o corrija los datos; si la cédula aún no se rellenó sola, puede estar extrayéndose.</p>
                  {fotoCedulaPreview && <img src={fotoCedulaPreview} alt="Cédula" className="max-h-28 rounded-lg border object-contain" />}
                  {loadingOcr && <OcrSpinnerRow />}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" value={nombreCedula} onChange={(e) => setNombreCedula(e.target.value)} placeholder="Nombre" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    <input type="text" value={cedulaNum} onChange={(e) => setCedulaNum(e.target.value)} placeholder="Cédula" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <input type="text" value={observacionVisita} onChange={(e) => setObservacionVisita(e.target.value)} placeholder="Descripción" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  <button type="button" onClick={openConfirmModal} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700">Guardar</button>
                </div>
              )}
            </div>
          )}

          {view === CAPTURA && flow === FLUJO_FUNCIONARIOS && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              {pasoCapturaFuncionarios === 'elegir' && (
                <div className="space-y-4">
                  <p className="text-2xl font-bold text-gray-900 mb-1">Nombre</p>
                  <p className="text-sm text-gray-600 mb-2">Elija un funcionario o agregue uno nuevo.</p>
                  {loadingCatalogo && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 py-1">
                      <span className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-600" aria-hidden />
                      <span>Cargando catálogo…</span>
                    </div>
                  )}
                  {!loadingCatalogo && (
                    <select
                      value={selectedFuncionarioCatalog ? String(selectedFuncionarioCatalog.id) : 'seleccione'}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'seleccione' || val === '') {
                          setSelectedFuncionarioCatalog(null);
                          setNombreCedula('');
                          setCedulaNum('');
                        } else if (val === 'agregar') {
                          setSelectedFuncionarioCatalog(null);
                          setNombreCedula('');
                          setCedulaNum('');
                          setFotoCedula(null);
                          setFotoCedulaPreview('');
                          setPasoCapturaFuncionarios('cedula');
                        } else {
                          const item = catalogoFuncionarios.find((f) => String(f.id) === val);
                          if (item) {
                            const cedulaDesc = parseCedulaFromDescripcion(item.Descripcion);
                            const completo = cedulaDesc.length === 10 && !!item.foto_url;
                            setNombreCedula(item.Dato || '');
                            setCedulaNum(cedulaDesc || '');
                            setSelectedFuncionarioCatalog(item);
                            if (completo) {
                              setShowConfirmModal(true);
                            } else {
                              setPasoCapturaFuncionarios('cedula');
                            }
                          }
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="seleccione">Seleccione</option>
                      <option value="agregar">Agregar</option>
                      {catalogoFuncionarios.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.Dato || '—'}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {pasoCapturaFuncionarios === 'cedula' && (
                <div className="space-y-4">
                  <p className="text-2xl font-bold text-gray-900 mb-1">Capturar cédula</p>
                  {!cameraAbierta ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (useDeviceCamera) inputCedulaRef.current?.click();
                        else startCameraDesktop();
                      }}
                      className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md"
                    >
                      Capturar
                    </button>
                  ) : (
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                      <canvas ref={canvasRef} className="hidden" />
                      <button
                        type="button"
                        onClick={async () => {
                          const f = await captureFromVideo();
                          if (f) handleCaptureCedula(f, () => setPasoCapturaFuncionarios('revision'));
                        }}
                        className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow"
                      >
                        Capturar
                      </button>
                    </div>
                  )}
                  <input
                    ref={inputCedulaRef}
                    type="file"
                    accept="image/*"
                    capture={useDeviceCamera ? 'environment' : undefined}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleCaptureCedula(f, () => setPasoCapturaFuncionarios('revision'));
                      e.target.value = '';
                    }}
                  />
                </div>
              )}

              {pasoCapturaFuncionarios === 'revision' && (
                <div className="space-y-4">
                  <p className="text-2xl font-bold text-gray-900 mb-1">Revisar datos</p>
                  <p className="text-sm text-gray-600">Verifique nombre y cédula; si vienen vacíos, espere un momento a la extracción.</p>
                  {fotoCedulaPreview && <img src={fotoCedulaPreview} alt="Cédula" className="max-h-28 rounded-lg border object-contain" />}
                  {loadingOcr && <OcrSpinnerRow />}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" value={nombreCedula} onChange={(e) => setNombreCedula(e.target.value)} placeholder="Nombre" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    <input type="text" value={cedulaNum} onChange={(e) => setCedulaNum(e.target.value)} placeholder="Cédula" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <button type="button" onClick={openConfirmModal} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700">Guardar</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-4 py-3 text-white rounded-t-xl" style={{ backgroundColor: envConfig.loginButtonColor || '#198754' }}>
              <h2 className="text-xl font-bold leading-tight">{flow === FLUJO_VISITA ? 'Visita' : 'Funcionarios'}</h2>
            </div>
            <div className="p-6 space-y-4 border border-t-0 border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-600">Confirme para guardar el registro.</p>
              {flow === FLUJO_FUNCIONARIOS && ((selectedFuncionarioCatalog?.foto_url && !fotoCedula) || fotoCedulaPreview) ? (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Foto cédula</p>
                  <img
                    src={
                      fotoCedulaPreview
                        ? fotoCedulaPreview
                        : selectedFuncionarioCatalog?.foto_url?.startsWith('http')
                          ? selectedFuncionarioCatalog.foto_url
                          : apiOrigin && selectedFuncionarioCatalog?.foto_url
                            ? `${apiOrigin}${selectedFuncionarioCatalog.foto_url.startsWith('/') ? '' : '/'}${selectedFuncionarioCatalog.foto_url}`
                            : ''
                    }
                    alt="Cédula"
                    className="w-full max-h-28 object-contain rounded-lg border border-gray-200 bg-gray-50"
                  />
                </div>
              ) : null}
              <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
                {flow === FLUJO_VISITA && <p><span className="font-medium text-gray-700">Descripción:</span> <strong>{observacionVisita || '—'}</strong></p>}
                <p><span className="font-medium text-gray-700">Nombre:</span> <strong>{nombreCedula || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Cédula:</span> <strong>{cedulaNum || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Foto cédula:</span> <strong>Adjunta</strong></p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGuardarVista}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50"
                  style={{ backgroundColor: envConfig.loginButtonColor || '#198754' }}
                >
                  {submitting ? 'Guardando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AlfaTango1;
